import { useSiteFileDownload } from "@inrange/building-manager-api-client";
import {
  Site,
  SiteFile,
} from "@inrange/building-manager-api-client/models-site";
import { NoPaddedRow } from "@inrange/theme-components";
import {
  DownloadFile,
  FileListTable,
  getSortingIcon,
  TableCellData,
} from "@inrange/theme-components/table";
import { DateTime } from "luxon";
import React, { useContext, useMemo, useState } from "react";
import { UserContext } from "../../auth/UserContext";
import { sortAscendingForColumn } from "../../views/SiteListSortMethods";

interface SiteDocumentsProps {
  site: Site;
  orgId: string;
}

const formatFileDate = (date: number): string => {
  return DateTime.fromSeconds(date).toFormat("d LLLL yyyy");
};

const SiteDocuments: React.FC<SiteDocumentsProps> = ({ site, orgId }) => {
  const { user } = useContext(UserContext);
  const [sortColumn, setSortColumn] = useState<string>("date");
  const [sortAscending, setSortAscending] = useState<boolean>(false);

  const documentFiles = useMemo(() => {
    const files = (site.siteFiles || []).filter(
      (file) => file.fileType === "siteDocument"
    );
    const sorted = [...files];
    sorted.sort((a, b) => {
      if (sortColumn === "description") {
        const aDesc = a.description || a.fileName;
        const bDesc = b.description || b.fileName;
        return sortAscending
          ? aDesc.localeCompare(bDesc)
          : bDesc.localeCompare(aDesc);
      }
      if (sortColumn === "date") {
        const aDate = a.date || a.updatedAt || 0;
        const bDate = b.date || b.updatedAt || 0;
        return sortAscending ? aDate - bDate : bDate - aDate;
      }
      return 0;
    });
    return sorted;
  }, [site.siteFiles, sortColumn, sortAscending]);

  const handleColumnClick = (columnKey: string) => {
    if (sortColumn === columnKey) {
      setSortAscending((prev) => !prev);
      return;
    }
    setSortColumn(columnKey);
    setSortAscending(sortAscendingForColumn(columnKey));
  };

  const tableHead = (
    <tr>
      <th onClick={() => handleColumnClick("description")}>
        Name {getSortingIcon(sortColumn, "description", sortAscending)}
      </th>
      <th>File type</th>
      <th onClick={() => handleColumnClick("date")}>
        Date {getSortingIcon(sortColumn, "date", sortAscending)}
      </th>
      <th></th>
    </tr>
  );

  const tableRows =
    documentFiles.length > 0 ? (
      documentFiles.map((file) => (
        <DocumentRow
          key={file.fileGuid}
          file={file}
          site={site}
          orgId={orgId}
          userEmail={user!.email}
        />
      ))
    ) : (
      <tr>
        <td colSpan={3} style={{ width: "70%" }}>
          <TableCellData>
            When your site has documents associated with it, you&apos;ll be able
            to access them here.
          </TableCellData>
        </td>
        <td align="right">
          <DownloadFile downloadDisabled={true} />
        </td>
      </tr>
    );

  return (
    <NoPaddedRow>
      <FileListTable head={tableHead} rows={tableRows} />
    </NoPaddedRow>
  );
};

const DocumentRow: React.FC<{
  file: SiteFile;
  site: Site;
  orgId: string;
  userEmail: string;
}> = ({ file, site, orgId, userEmail }) => {
  const { fetchSiteFileURL } = useSiteFileDownload({
    siteId: site.id,
    file,
    isAttachment: true,
    enabled: false,
    userOrgId: orgId,
  });

  const description = file.description || file.fileName;
  const extension = file.fileName.split(".").pop()?.toUpperCase() || "";
  const date = file.date || file.updatedAt;

  return (
    <tr key={file.fileGuid} id={file.fileGuid} data-testid={`document-row-${file.fileName}`}>
      <td align="left">
        <TableCellData>{description}</TableCellData>
      </td>
      <td align="left">
        <TableCellData>{extension}</TableCellData>
      </td>
      <td align="left">
        <TableCellData>{date ? formatFileDate(date) : "-"}</TableCellData>
      </td>
      <td align="right">
        <DownloadFile
          downloadDisabled={fetchSiteFileURL.isInitialLoading}
          downloadURL={fetchSiteFileURL.data?.url}
          fileName={file.fileName}
          userEmail={userEmail}
          site={site}
          refetchURL={fetchSiteFileURL.refetch}
        />
      </td>
    </tr>
  );
};

export default SiteDocuments;
